import React from 'react';
import { Star, Target, Users, TrendingUp, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Star className="w-8 h-8 text-[#A0704E] fill-[#A0704E]" />
            <span className="text-2xl font-bold text-[#A0704E]">MARKETRY</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Building for Adaptation</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Marketry is the consistent effort of a young brand which assertively navigates rapid change, 
            bringing fresh student perspectives to the Australian marketing landscape.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Marketry was born from a simple observation: there was a gap between academic marketing knowledge 
                  and real-world application in the Australian market. Students had fresh perspectives and rigorous 
                  research skills, while businesses needed actionable insights.
                </p>
                <p className="text-lg">
                  We bridge this gap by creating a platform where student-led research meets practical business strategy. 
                  Our bi-weekly reports dissect every touchpoint in the customer journey, providing both students and 
                  businesses with the insights they need to succeed.
                </p>
                <p className="text-lg">
                  The Lac bird in our logo symbolizes guidance through hardship—a fitting metaphor for navigating 
                  the complexities of modern marketing. The star represents our wholehearted dedication to rapid growth 
                  and our eagerness to overcome challenges.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#A0704E] to-[#8B5F3E] rounded-2xl p-12 text-white">
                <Star className="w-16 h-16 mb-6 fill-white" />
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-amber-100 text-lg leading-relaxed">
                  To provide actionable insights on every customer touchpoint in the Australian market, 
                  empowering students and businesses to make informed marketing decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Do</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We create a seamless customer journey where our reports work as touchpoints to help guide targeting strategies
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-gray-100 hover:border-[#A0704E] transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-[#A0704E]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Students</h3>
                <p className="text-gray-700 leading-relaxed">
                  Access real-world marketing insights for your IMC projects, startup initiatives, and coursework. 
                  Learn how theory applies to the Australian market through detailed touchpoint analysis.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#A0704E] transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                  <TrendingUp className="w-6 h-6 text-[#A0704E]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Businesses</h3>
                <p className="text-gray-700 leading-relaxed">
                  Get fresh insights from the student perspective combined with rigorous market research. 
                  Perfect for startups, SMEs, and corporate growth teams planning Australian market entry or expansion.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Approach</h2>
            <p className="text-xl text-gray-600">How we deliver value through every touchpoint</p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#A0704E] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Student-Led Research</h3>
              <p className="text-gray-600 leading-relaxed">
                Marketing students from top Australian universities conduct primary research, bringing fresh perspectives 
                and cutting-edge academic knowledge to real-world problems.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#A0704E] rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Touchpoint Analysis</h3>
              <p className="text-gray-600 leading-relaxed">
                We map every customer touchpoint in the journey, identifying critical moments where brands can 
                strengthen connections and drive conversions in the Australian market.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#A0704E] rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Actionable Insights</h3>
              <p className="text-gray-600 leading-relaxed">
                Every report includes practical recommendations that can be immediately applied to marketing projects, 
                campaigns, and business strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
          </div>

          <div className="space-y-6">
            <Card className="border-l-4 border-[#A0704E]">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Adaptation</h3>
                <p className="text-gray-700">We embrace change and continuously evolve our research methods to stay relevant in a rapidly changing market.</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-[#A0704E]">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fresh Perspectives</h3>
                <p className="text-gray-700">We value the unique insights that come from student researchers who aren't constrained by industry conventions.</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-[#A0704E]">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Rigor</h3>
                <p className="text-gray-700">Every report is backed by solid research methodology, combining primary and secondary data for credible insights.</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-[#A0704E]">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Accessibility</h3>
                <p className="text-gray-700">Quality marketing insights shouldn't be locked behind expensive consultancy fees. We make knowledge accessible.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#A0704E] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Join Us on the Journey</h2>
          <p className="text-xl text-amber-100 mb-8">
            Whether you're a student or a business, we're here to guide you through every touchpoint
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reports">
              <Button size="lg" className="bg-white text-[#A0704E] hover:bg-gray-100 px-8 py-6 text-lg">
                Explore Reports
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#A0704E] px-8 py-6 text-lg">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;